import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRight, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import GoogleSignInButton from "@/components/GoogleSignInButton";

const ORG_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "nonprofit", label: "Nonprofit / 501(c)(3)" },
  { value: "school", label: "School / University" },
  { value: "church", label: "Church / Faith-based" },
  { value: "business", label: "Small Business" },
  { value: "municipality", label: "Municipality / Government" },
];

export default function Signup() {
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", organization_type: "nonprofit",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await signup(form);
      toast.success("Welcome to Beacon of Grants!");
      // If they came from the /pricing trial picker, resume checkout immediately
      const pendingPlan = sessionStorage.getItem("bg_pending_plan");
      if (pendingPlan) {
        sessionStorage.removeItem("bg_pending_plan");
        try {
          const res = await import("@/lib/api").then(m => m.api.post("/payments/checkout", {
            lookup_key: pendingPlan,
            origin_url: window.location.origin,
          }));
          window.location.href = res.data.checkout_url;
          return;
        } catch (err) {
          toast.error("Signed up, but couldn't start checkout. Try again from the pricing page.");
        }
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex" data-testid="signup-page">
      <div className="hidden lg:flex lg:w-[45%] bg-[#1C3F35] text-white p-12 flex-col justify-between relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2.5 relative z-10">
          <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-heading text-lg tracking-tight">Beacon of Grants</span>
        </Link>
        <div className="relative z-10">
          <div className="label-eyebrow text-white/60">Start free</div>
          <h2 className="font-heading font-light text-4xl mt-4 leading-tight">
            From application<br/>anxiety to <span className="text-[#C1654B]">awarded</span>.
          </h2>
          <ul className="mt-8 space-y-3 text-white/80">
            <li>· 7-day free trial, then $29/mo — 3 active grants</li>
            <li>· Every federal, foundation & corporate grant</li>
            <li>· AI writer with Claude Sonnet 4.5</li>
            <li>· Deadline reminders in your inbox</li>
          </ul>
        </div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#C1654B]/20 blur-3xl"/>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-md bg-[#1C3F35] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-heading text-lg">Beacon of Grants</span>
          </Link>

          <div className="label-eyebrow mb-3">Create account</div>
          <h1 className="font-heading font-light text-4xl tracking-tighter text-[#1A1A1A]">
            Create your account
          </h1>
          <p className="text-[#5E5E5E] mt-2">Already have one? <Link to="/login" className="text-[#1C3F35] underline underline-offset-4" data-testid="signup-to-login">Sign in</Link></p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <GoogleSignInButton testId="signup-google" />
            <div className="flex items-center gap-3 my-2">
              <div className="h-px flex-1 bg-[#E6E4DF]"/>
              <span className="text-[10px] uppercase tracking-widest text-[#7A8C77] font-bold">or with email</span>
              <div className="h-px flex-1 bg-[#E6E4DF]"/>
            </div>
            <div>
              <Label htmlFor="full_name" className="text-[#1A1A1A]">Full name</Label>
              <Input
                id="full_name" required value={form.full_name}
                onChange={(e)=>setForm({...form, full_name: e.target.value})}
                data-testid="signup-fullname"
                className="mt-2 bg-white border-[#E6E4DF]" placeholder="Jane Rivera"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-[#1A1A1A]">Email</Label>
              <Input
                id="email" type="email" required value={form.email}
                onChange={(e)=>setForm({...form, email: e.target.value})}
                data-testid="signup-email"
                className="mt-2 bg-white border-[#E6E4DF]" placeholder="jane@nonprofit.org"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#1A1A1A]">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password" type={showPassword ? "text" : "password"} required minLength={6}
                  value={form.password}
                  onChange={(e)=>setForm({...form, password: e.target.value})}
                  data-testid="signup-password"
                  className="bg-white border-[#E6E4DF] pr-10" placeholder="At least 6 characters"
                />
                <button
                  type="button"
                  onClick={()=>setShowPassword((s)=>!s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="signup-password-toggle"
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-[#5E5E5E] hover:text-[#1C3F35] transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm_password" className="text-[#1A1A1A]">Confirm password</Label>
              <div className="relative mt-2">
                <Input
                  id="confirm_password" type={showConfirm ? "text" : "password"} required minLength={6}
                  value={confirmPassword}
                  onChange={(e)=>setConfirmPassword(e.target.value)}
                  data-testid="signup-confirm-password"
                  className="bg-white border-[#E6E4DF] pr-10" placeholder="Re-enter your password"
                />
                <button
                  type="button"
                  onClick={()=>setShowConfirm((s)=>!s)}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  data-testid="signup-confirm-password-toggle"
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-[#5E5E5E] hover:text-[#1C3F35] transition-colors"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
              </div>
              {confirmPassword && form.password !== confirmPassword && (
                <p className="mt-1.5 text-xs text-[#C1654B]" data-testid="signup-password-mismatch">
                  Passwords don't match yet.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="org_type" className="text-[#1A1A1A]">Organization type</Label>
              <Select
                value={form.organization_type}
                onValueChange={(v)=>setForm({...form, organization_type: v})}
              >
                <SelectTrigger id="org_type" data-testid="signup-org-type" className="mt-2 bg-white border-[#E6E4DF]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORG_TYPES.map((o)=> (
                    <SelectItem key={o.value} value={o.value} data-testid={`org-option-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="signup-submit"
              className="w-full bg-[#1C3F35] text-white hover:bg-[#1A362D] rounded-md py-6"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Create account <ArrowRight className="w-4 h-4 ml-2"/></>}
            </Button>
            <p className="text-xs text-[#5E5E5E] text-center">
              By signing up you agree to our terms & privacy policy.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
